import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { AlertController } from '@ionic/angular';
import { MaskitoElementPredicate, MaskitoOptions, maskitoTransform } from '@maskito/core';
import { select, Store } from '@ngrx/store';

import { map, Observable } from 'rxjs';

import { phoneMask } from '../../constants/maskito.constants';
import { GenericComponent } from '../../misc/components/generic/generic.component';
import * as fromUserActions from '../../store/actions/user.actions';
import * as fromUserSelectors from '../../store/selectors/user.selectors';

interface PersonalDetailsFormGroup {
  company: FormControl<string | null>;
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  phone: FormControl<string | null>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-personal-details',
  standalone: false,
  templateUrl: './personal-details.page.html'
})
export class PersonalDetailsPageComponent extends GenericComponent {
  private store = inject(Store);
  private alertController = inject(AlertController);
  private changeDetectorRef = inject(ChangeDetectorRef);

  email$: Observable<string | null>;
  personalDetailsForm: FormGroup<PersonalDetailsFormGroup> = new FormGroup<PersonalDetailsFormGroup>({
    company: new FormControl<string>('', Validators.required),
    firstName: new FormControl<string>('', Validators.required),
    lastName: new FormControl<string>('', Validators.required),
    phone: new FormControl<string>('', Validators.required)
  });

  readonly phoneMask: MaskitoOptions = phoneMask;
  readonly maskPredicate: MaskitoElementPredicate = async (el) =>
    (el as unknown as HTMLIonInputElement).getInputElement();

  constructor() {
    super();
    this.email$ = this.store.pipe(
      select(fromUserSelectors.selectDetails),
      map((userDetails) => userDetails.email)
    );
  }

  ionViewWillEnter() {
    this.store.dispatch(fromUserActions.reloadMobileUser());
  }

  ionViewDidEnter() {
    this.addUniqueSubscription(
      'selectUserDetails',
      this.store.pipe(select(fromUserSelectors.selectDetails)).subscribe((userDetails) => {
        let phone = '';
        if (userDetails.phone) {
          phone = maskitoTransform(userDetails.phone, this.phoneMask);
        }
        this.personalDetailsForm.setValue({
          company: userDetails.company || '',
          firstName: userDetails.firstName || '',
          lastName: userDetails.lastName || '',
          phone
        });
        this.personalDetailsForm.markAsPristine();
        this.personalDetailsForm.updateValueAndValidity();
        this.changeDetectorRef.detectChanges();
      })
    );
  }

  onSubmit() {
    this.store.dispatch(
      fromUserActions.updateMobileUser({
        payload: {
          company: this.personalDetailsForm.get('company')?.value || '',
          firstName: this.personalDetailsForm.get('firstName')?.value || '',
          lastName: this.personalDetailsForm.get('lastName')?.value || '',
          phone: (this.personalDetailsForm.get('phone')?.value || '').replace(/\D/g, '')
        }
      })
    );
  }

  async deleteMyData() {
    const alert = await this.alertController.create({
      buttons: [
        {
          role: 'cancel',
          text: 'Cancel'
        },
        {
          role: 'destructive',
          text: 'Delete'
        }
      ],
      header: 'Delete My Data',
      message:
        'Are you sure? Your account and all of your saved profiles and farms will be deleted. This action cannot be undone.'
    });

    await alert.present();

    const { role } = await alert.onDidDismiss();
    if (role === 'destructive') {
      this.store.dispatch(fromUserActions.deleteData());
    }
  }
}
